import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { tariffsApi } from '../../services/settingsApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function TariffsTab() {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTariffs, setFilteredTariffs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [selectedGate, setSelectedGate] = useState('all'); // Filter by gate
  const [formData, setFormData] = useState({
    name: '',
    vehicle_type: '',
    kip_rate: '',
    bath_rate: '',
    gate: '',
    status: 'active'
  });
  const { getMessage, getCommonLabel } = useTranslation();

  useEffect(() => {
    fetchTariffs();
  }, []);

  useEffect(() => {
    let filtered = tariffs;
    
    // Filter by gate (extracted from name)
    if (selectedGate !== 'all') {
      filtered = filtered.filter(tariff => {
        const gateCode = tariff.name ? tariff.name.substring(0, 3).toUpperCase() : '';
        return gateCode === selectedGate;
      });
    }
    
    // Filter by search query
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(tariff =>
        tariff.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tariff.vehicle_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tariff.gate?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredTariffs(filtered);
  }, [searchQuery, tariffs, selectedGate]);

  const fetchTariffs = async () => {
    try {
      setLoading(true);
      console.log('Fetching tariffs...');
      const response = await tariffsApi.getAll();
      console.log('Tariffs API response:', response);
      console.log('Response data:', response.data);
      
      // Try different response structures
      let tariffsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          tariffsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          tariffsData = response.data.data;
        } else if (response.data.tariffs && Array.isArray(response.data.tariffs)) {
          tariffsData = response.data.tariffs;
        }
      }
      
      console.log('Parsed tariffs data:', tariffsData);
      console.log('Total tariffs found:', tariffsData.length);
      
      setTariffs(tariffsData);
      
      if (tariffsData.length === 0) {
        console.warn('No tariffs found in response');
      }
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Failed to load tariffs: ${error.response?.data?.message || error.message}`);
      setTariffs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddTariff = () => {
    setEditingTariff(null);
    setFormData({
      name: '',
      vehicle_type: '',
      kip_rate: '',
      bath_rate: '',
      gate: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditTariff = (tariff) => {
    setEditingTariff(tariff);
    setFormData({
      name: tariff.name || '',
      vehicle_type: tariff.vehicle_type || '',
      kip_rate: tariff.kip_rate || '',
      bath_rate: tariff.bath_rate || '',
      gate: tariff.gate || '',
      status: tariff.status || 'active'
    });
    setShowModal(true);
  };

  const handleDeleteTariff = async (tariff) => {
    if (!window.confirm('Are you sure you want to delete this tariff?')) {
      return;
    }

    try {
      await tariffsApi.delete(tariff.id);
      toast.success('Tariff deleted successfully');
      fetchTariffs();
    } catch (error) {
      console.error('Error deleting tariff:', error);
      toast.error('Failed to delete tariff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        kip_rate: formData.kip_rate ? parseFloat(formData.kip_rate) : 0,
        bath_rate: formData.bath_rate ? parseFloat(formData.bath_rate) : 0
      };

      if (editingTariff) {
        await tariffsApi.update(editingTariff.id, data);
        toast.success('Tariff updated successfully');
      } else {
        await tariffsApi.create(data);
        toast.success('Tariff created successfully');
      }
      
      setShowModal(false);
      fetchTariffs();
    } catch (error) {
      console.error('Error saving tariff:', error);
      toast.error('Failed to save tariff');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTariff(null);
  };

  const vehicleTypes = [
    { key: 'small_car', label: 'Small Car' },
    { key: 'compact_car_truck', label: 'Compact Car/Truck' },
    { key: 'small_bus', label: 'Small Bus (7-12 seats)' },
    { key: 'medium_bus', label: 'Medium Bus (12-24 seats)' },
    { key: 'large_bus', label: 'Large Bus (>24 seats)' },
    { key: 'truck_6_wheels', label: 'Truck 6 Wheels' },
    { key: 'truck_10_wheels', label: 'Truck 10 Wheels' },
    { key: 'truck_10_plus_wheels', label: 'Truck 10+ Wheels' }
  ];
  
  const gates = [
    { code: 'SVK', name: 'Bokeo Gate (SVK)', icon: '🏔️' },
    { code: 'VTE', name: 'Vientiane Gate (VTE)', icon: '🏛️' },
    { code: 'KHM', name: 'Khammouan Gate (KHM)', icon: '🌄' },
    { code: 'SVK', name: 'Savannakhet Gate (SVK)', icon: '🌉' },
    { code: 'BLK', name: 'Bolikhamxay Gate (BLK)', icon: '🏞️' }
  ];
  
  // Extract gate code from tariff name (first 3 characters)
  const getGateCode = (tariffName) => {
    if (!tariffName) return null;
    return tariffName.substring(0, 3).toUpperCase();
  };
  
  // Group tariffs by gate (extracted from name)
  const groupedTariffs = gates.map(gate => ({
    ...gate,
    tariffs: filteredTariffs.filter(t => getGateCode(t.name) === gate.code)
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            💰 {getMessage('settings.tariffs') || 'Tariffs'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {getMessage('settings.tariffs_desc') || 'Manage toll pricing and tariff structures'}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchTariffs}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            🔄 {getMessage('common.refresh') || 'Refresh'}
          </button>
          <button
            onClick={handleAddTariff}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            ➕ {getCommonLabel('add') || 'Add Tariff'}
          </button>
        </div>
      </div>

      {/* Search and Gate Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder={getMessage('common.search') || 'Search tariffs...'}
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
            🔍
          </div>
        </div>
        
        {/* Gate Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedGate('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
              selectedGate === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
            }`}
          >
            🌍 All Gates
          </button>
          {gates.map(gate => (
            <button
              key={gate.code}
              onClick={() => setSelectedGate(gate.code)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                selectedGate === gate.code
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
              }`}
            >
              {gate.icon} {gate.code}
            </button>
          ))}
        </div>
      </div>
      
      {/* Summary Stats & Debug Info */}
      <div className="space-y-2">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredTariffs.length} tariff(s)
          {selectedGate !== 'all' && ` for ${gates.find(g => g.code === selectedGate)?.name}`}
        </div>
        {/* Debug Info - Remove this after fixing */}
        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
          Total in database: {tariffs.length} | Filtered: {filteredTariffs.length} | Selected Gate: {selectedGate}
        </div>
      </div>

      {/* Tariffs by Gate - List View */}
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        ) : filteredTariffs.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-700 rounded-lg">
            <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">💰</div>
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery || selectedGate !== 'all' ? 'No tariffs found matching your filters' : 'No tariffs available'}
            </p>
          </div>
        ) : (
          groupedTariffs.map((gateGroup) => {
            if (gateGroup.tariffs.length === 0) return null;
            
            return (
              <div key={gateGroup.code} className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-hidden">
                {/* Gate Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{gateGroup.icon}</span>
                      <div>
                        <h3 className="text-xl font-bold">{gateGroup.name}</h3>
                        <p className="text-blue-100 text-sm">Gate Code: {gateGroup.code}</p>
                      </div>
                    </div>
                    <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
                      <span className="text-2xl font-bold">{gateGroup.tariffs.length}</span>
                      <span className="text-sm ml-2">Tariff{gateGroup.tariffs.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>

                {/* Tariffs Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tariff Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Vehicle Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Kip Rate (LAK)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Baht Rate (THB)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                      {gateGroup.tariffs.map((tariff) => (
                        <tr key={tariff.id} className="hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                  {tariff.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  Tariff ID: #{tariff.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {tariff.vehicle_type}
                                </div>
                                {tariff.gate && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {tariff.gate}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-green-600 dark:text-green-400">
                              {new Intl.NumberFormat('lo-LA').format(parseFloat(tariff.kip_rate || 0))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                              {new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 2 }).format(parseFloat(tariff.bath_rate || 0))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              tariff.status === 'active' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                tariff.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                              }`}></span>
                              {tariff.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEditTariff(tariff)}
                              className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 mr-3"
                              title="Edit"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTariff(tariff)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete"
                            >
                              🗑️ Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingTariff ? 'Edit Tariff' : 'Add New Tariff'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name * <span className="text-xs text-gray-500">(Format: XXX-YYYY, e.g., VTE-NK01, SVK-NK01)</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., VTE-NK01, SVK-MD01, KHM-XX01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Start with gate code: <strong>VTE</strong>, <strong>SVK</strong>, <strong>KHM</strong>, <strong>SVK</strong>, or <strong>BLK</strong>
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle Type *
                </label>
                <select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select vehicle type</option>
                  {vehicleTypes.map(type => (
                    <option key={type.key} value={type.key}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kip Rate (LAK) *
                </label>
                <input
                  type="number"
                  name="kip_rate"
                  value={formData.kip_rate}
                  onChange={handleInputChange}
                  step="1"
                  min="0"
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Baht Rate (THB)
                </label>
                <input
                  type="number"
                  name="bath_rate"
                  value={formData.bath_rate}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gate
                </label>
                <input
                  type="text"
                  name="gate"
                  value={formData.gate}
                  onChange={handleInputChange}
                  placeholder="e.g., North Gate, South Gate"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  {editingTariff ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}